// Considered: no existing helper — the Activity/Timeline tools rendered a raw type label plus a
// property bag (specs/stream-trajectory-tools.md Problem Statement); there was no sentence
// grammar to reuse. Pure function so the actor-relation branching is unit-testable without
// mounting a component. See design/metaspace-tools/README.md §1d.
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx (`MSEntry` sentence composition).

import type { StreamEntry, StreamSubject } from 'types/stream';

/** Who the sentence's grammatical subject is, relative to the acting identity. */
export type StreamActorRelation = 'self' | 'other' | 'system';

export type StreamTranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Resolves the actor-subject relation that drives sentence grammar (README §1d / spec SC3):
 * - `self` — the entry is about the actor themselves (e.g. "Mike joined").
 * - `other` — a human actor acted on a different subject (e.g. "Sarah assigned Mike").
 * - `system` — no meaningful human actor; the aspect/subject was acted on by the system/reducer
 *   (e.g. "Mike was assigned Responsible ⚙").
 */
export function resolveStreamActorRelation(entry: StreamEntry, subject: StreamSubject | null): StreamActorRelation {
  if (subject?.kind === 'system') return 'system';
  if (!subject) {
    // No distinct subject: aspect_observation-style entries with no human actor of record are
    // treated as system-originated when their provenance says so; otherwise the actor is
    // speaking about themselves/the sphere directly.
    return entry.provenanceTier === 'calculated' || entry.provenanceTier === 'inferred' ? 'system' : 'self';
  }
  if (subject.id === entry.actor) return 'self';
  return 'other';
}

/**
 * Builds the Stream entry's verb-clause sentence fragment — the muted grammar between the
 * actor's name (rendered separately by `StreamEntryMolecule` via its own avatar/name span,
 * per "the avatar always belongs to the actor" — SC3) and the object/aspect name (also rendered
 * separately, since it carries its own inspect/jump-to-track click target).
 *
 * Falls back to a generic `<entryType>_<relation>` key with a safe default so entry types beyond
 * the two the acceptance tests exercise (`perspective_assigned`, `aspect_observation`) never
 * crash the Stream — they simply render an unstyled but sensible verb.
 */
export function buildStreamSentence(
  entry: StreamEntry,
  subject: StreamSubject | null,
  t: StreamTranslateFn
): string {
  const relation = resolveStreamActorRelation(entry, subject);
  const key = `stream.sentences.${entry.entryType}_${relation}`;

  const base = t(key, {
    actor: entry.actorName,
    subject: subject?.name ?? entry.actorName,
    defaultValue: t('stream.sentences.default_' + relation, {
      actor: entry.actorName,
      subject: subject?.name ?? entry.actorName,
      defaultValue: relation === 'system' ? 'was updated' : 'updated',
    }),
  });

  if (entry.delegated) {
    return `${base}${t('stream.sentences.delegatedSuffix', { defaultValue: ' · via delegation' })}`;
  }

  return base;
}

export default buildStreamSentence;
